const en = {
  common: {
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving…',
    saved: 'Saved',
    saveFailed: 'Save failed',
    delete: 'Delete',
    confirm: 'Confirm',
    pleaseWait: 'Please wait…',
    loading: 'Loading…',
    add: 'Add',
    adding: 'Adding…',
    create: 'Create',
    creating: 'Creating…',
    close: 'Close',
    post: 'Post',
    retry: 'Retry',
    unknown: 'Unknown',
    online: 'Live',
    offline: 'Offline',
    yes: 'Yes',
    no: 'No',
  },
  auth: {
    badge: '01 / Sign In',
    welcomeBack: 'Welcome back.',
    emailLabel: 'Email or username',
    passwordLabel: 'Password',
    signIn: 'Sign In',
    signingIn: 'Please wait...',
    signedInToast: 'Welcome back',
    loginHint: 'Login with your Anahub account.',
    failed: 'Authentication failed',
    heroLine1: 'Tickets in.',
    heroLine2: 'Merge requests out.',
    heroSubtitle:
      'CMR is a minimalist Kanban-style board with a built-in AI engineer that clones your repo, fixes tickets, and opens MRs on GitLab.',
    features: {
      dnd: 'Drag & drop',
      multiUser: 'Multi-user',
      claude: 'Claude Sonnet 4.5',
      gitlab: 'GitLab MRs',
    },
    footer: 'v1.0 // Built with Claude Sonnet 4.5',
  },
  sidebar: {
    projects: 'Projects',
    newProject: 'New project',
    noProjects: 'No projects yet. Create your first one.',
    members: 'Members',
    signOut: 'Sign out',
    language: 'Language',
    russian: 'Русский',
    english: 'English',
    stats: 'Leaderboard',
  },
  stats: {
    badge: 'Team stats',
    title: 'Developer Leaderboard',
    subtitle: 'Track commits, lines of code, comments, and tickets across all your projects.',
    linesAdded: 'Lines added',
    commits: 'Commits',
    comments: 'Comments',
    tickets: 'Tickets',
    streak: 'Streak',
    activeDays: 'Active days',
    days: 'days',
    periodAllTime: 'All time',
    periodWeek: 'This week',
    periodMonth: 'This month',
    longestStreak: 'Longest streak',
    badges: 'Badges',
    badgeLocked: 'Not earned yet',
    player: 'Developer',
    sync: 'Sync GitLab',
    syncing: 'Syncing…',
    syncDone: 'GitLab sync complete',
    syncFailed: 'Sync failed',
    loadFailed: 'Could not load stats',
    lastSync: 'Last sync',
    empty: 'No activity yet — create tickets, comment, or commit to GitLab.',
    emailHint:
      'Git commits are matched to your account by author email. Use the same email in Git as in your CMR login.',
    activityTitle: 'Commit activity',
    activityHint: 'Click a developer in the table to view their daily commits and lines added.',
    activityEmpty: 'No commits in this period.',
    activityLoadFailed: 'Could not load activity chart',
    closeChart: 'Close',
  },
  newProject: {
    title: 'New Project',
    description:
      'Connect one or more GitLab repos so the AI agent can clone them and open Merge Requests for tickets.',
    nameLabel: 'Name',
    descriptionLabel: 'Description',
    gitlabUrlLabel: 'GitLab Repo URL',
    gitlabUrlHint:
      'HTTPS ({httpsExample}) or SSH-style ({sshExample}). Don\'t paste your email or a web page URL.',
    gitlabTokenLabel: 'GitLab Personal Access Token',
    gitlabTokenHint: 'Needs {api} + {writeRepo} scopes.',
    gitlabTokenInfo:
      'After you create the project, the API clones each repo into a cache folder on the server. Set {envVar} on the server to control where that folder lives (default: {defaultDir}).',
    defaultBranchLabel: 'Default Branch',
    reposTitle: 'Repositories',
    reposHint:
      'You can connect multiple repos (e.g. backend + frontend) — the agent will fan out to all of them per ticket.',
    reposOptionalHint:
      'Optional — you can create the project now and add repositories later from Settings.',
    repoNameLabel: 'Name',
    repoNamePlaceholder: 'backend, frontend, …',
    addRepoBtn: 'Add another repo',
    removeRepoAria: 'Remove this repository',
    submit: 'Create Project',
    submitting: 'Creating...',
    createdToast: 'Project "{name}" created',
    failedToast: 'Failed to create project',
  },
  personalTokens: {
    title: 'Personal access tokens',
    description:
      'A personal token acts as you in every project you are a member of — for your own scripts and AI coding agents (Claude Code, Cursor). Comments it posts appear under your name.',
    namePlaceholder: 'Laptop — Claude Code',
    expiryLabel: 'Expires',
    expiryDays: '{days} days',
    expiryNever: 'Never',
    createBtn: 'Create token',
    empty: 'No personal tokens yet.',
    loadFailedToast: 'Failed to load tokens',
    createFailedToast: 'Failed to create token',
    revokedToast: 'Token revoked',
    revokeFailedToast: 'Failed to revoke token',
    revokeConfirmTitle: 'Revoke this token?',
    revokeConfirmDescription: 'Revoke "{name}". Anything using it stops working immediately.',
    revokeConfirmBtn: 'Revoke token',
    createdDialogTitle: 'Save your personal token',
    expiresAt: 'expires {when}',
    noExpiry: 'no expiry',
    example:
      'curl {base}/integrations/me -H "Authorization: Bearer cmr_pat_…"\n\n# Claude Code via cmr-mcp:\nclaude mcp add cmr -e CMR_BASE_URL={base} -e CMR_TOKEN=cmr_pat_… -- cmr-mcp',
  },
  settings: {
    button: 'Settings',
    title: 'Project Settings',
    description: 'Edit project metadata, manage connected repositories, members.',
    nameLabel: 'Name',
    descriptionLabel: 'Description',
    codebaseContextLabel: 'Codebase context (for chat assistant)',
    codebaseContextHint: 'Paste a short map of your codebase — key files, folders, and what they do. The chat assistant uses this to name the files a ticket should touch.',
    codebaseContextPlaceholder: 'e.g.\nbackend/app/modules/tickets/ — ticket CRUD + Kanban moves\nbackend/app/modules/assistant/ — chat PM assistant\nfrontend/src/pages/ProjectBoard.jsx — the board UI',
    digestLabel: 'Daily standup in chat',
    digestHint: 'Claude posts a daily standup to project chat — stale tickets, long-running reviews, and unassigned work.',
    gitlabUrlLabel: 'GitLab Repo URL',
    gitlabUrlHint: "HTTPS or SSH-style only. Don't paste your email or a web page.",
    gitlabTokenLabel: 'GitLab Token',
    gitlabTokenPlaceholder: '(leave empty to keep current)',
    gitlabTokenHint: 'Needs {api} + {writeRepo} scopes.',
    defaultBranchLabel: 'Default Branch',
    saveMetaBtn: 'Save name & description',
    reposTitle: 'Repositories',
    reposDescription:
      'GitLab repos the AI agent works with on this project. Add backend + frontend (or more) and the agent will fan out across them per ticket.',
    reposEmptyOwnerHint:
      'No repositories connected yet. Paste a GitLab URL and Personal Access Token below to enable cloning, branch creation on "in progress", and agent runs.',
    reposEmptyMemberHint:
      'No repositories connected yet. The project owner needs to connect at least one repo before the agent can run.',
    connectFirstRepoBtn: 'Connect a repository',
    gitlabUrlFormatHint:
      'HTTPS like https://gitlab.com/group/project or SSH like git@gitlab.com:group/project.git. Not an email or a web page URL.',
    gitlabUrlError: {
      empty: 'GitLab URL is required.',
      format:
        'Not a valid Git URL. Use HTTPS (https://gitlab.com/group/project) or SSH (git@gitlab.com:group/project.git). You pasted an email or web address.',
      incomplete: 'URL is missing the group/project path (e.g. https://gitlab.com/group/project).',
    },
    recloneBlockedByUrl: 'Fix the GitLab URL and save before re-cloning.',
    repoMissingUrlWarning:
      'No GitLab URL set for this repository. Type one in the field below and click Save repo. The agent will clone it on its first run for this repo.',
    repoNameLabel: 'Name',
    repoNamePlaceholder: 'backend, frontend, …',
    branchPrefixLabel: 'Branch prefix',
    branchPrefixPlaceholder: 'feat',
    branchPrefixHint:
      'Used in branch names: {prefix}-{ticket}. Leave empty to fall back to the project name. Cyrillic is transliterated (Добавить → dobavit).',
    repoSaveBtn: 'Save repo',
    repoSavingBtn: 'Saving…',
    repoSavedToast: 'Repository updated',
    repoSaveFailedToast: 'Failed to update repository',
    repoRemoveBtn: 'Remove',
    repoRemoveConfirmTitle: 'Remove this repository?',
    repoRemoveConfirmDescription:
      'Remove "{name}" from this project. The agent will no longer run on this repo. This cannot be undone.',
    repoRemoveConfirmBtn: 'Remove repository',
    repoRemovedToast: 'Repository removed',
    repoRemoveFailedToast: 'Failed to remove repository',
    repoAddedToast: 'Repository added — it will be cloned on the first agent run.',
    repoAddFailedToast: 'Failed to add repository',
    repoMinHint: 'A project must have at least one repository.',
    addRepoBtn: 'Add another repo',
    addingRepoBtn: 'Adding…',
    addRepoSubmitBtn: 'Connect repo',
    recloneBtn: 'Re-clone',
    recloneStarting: 'Starting…',
    recloneStartedToast: 'Re-clone started',
    recloneFailedToast: 'Failed to re-clone',
    lastClonedAtShort: 'Cloned {when}',
    repoStatus: {
      idle: 'Not cloned',
      cloning: 'Cloning…',
      ready: 'Ready',
      failed: 'Failed',
    },
    apiKeys: {
      title: 'API integration',
      description:
        'Generate a project API key so other apps can create, update, and move tickets via the integration API.',
      nameLabel: 'Key name',
      namePlaceholder: 'Main app, CI bot, …',
      createBtn: 'Generate key',
      creatingBtn: 'Generating…',
      empty: 'No API keys yet.',
      ownerOnlyHint: 'Only the project owner can manage API keys.',
      createdToast: 'API key created',
      createFailedToast: 'Failed to create API key',
      loadFailedToast: 'Failed to load API keys',
      revokedToast: 'API key revoked',
      revokeFailedToast: 'Failed to revoke API key',
      revokeConfirmTitle: 'Revoke this API key?',
      revokeConfirmDescription:
        'Revoke "{name}". Any integration using this key will stop working immediately.',
      revokeConfirmBtn: 'Revoke key',
      createdDialogTitle: 'Save your API key',
      createdDialogDescription:
        'This secret is shown only once. Copy it now — you cannot view it again after closing.',
      secretLabel: 'API key',
      copiedToast: 'Copied to clipboard',
      copyFailedToast: 'Could not copy',
      createdAt: 'Created {when}',
      lastUsedAt: 'Last used {when}',
      example:
        'GET …/tickets/by-assignee?email=user@company.com\nGET …/tickets/by-creator?email=user@company.com\nGET …/tickets?user_email=user@company.com\nX-CMR-API-Key: cmr_live_…',
    },
    membersTitle: 'Members',
    save: 'Save',
    saving: 'Saving…',
    saveSuccessToast: 'Project updated',
    saveFailedToast: 'Failed to save project',
    deleteBtn: 'Delete project',
    deleteConfirmTitle: 'Delete this project?',
    deleteConfirmDescription:
      'Delete "{name}" and all of its tickets and comments. This cannot be undone.',
    deleteConfirmBtn: 'Delete project',
    deletedToast: 'Project deleted',
    deleteFailedToast: 'Failed to delete project',
  },
  members: {
    title: 'Members',
    dialogDescription:
      'Owners can add or remove members. Members see this project, create tickets, and trigger the agent.',
    none: 'No members yet.',
    ownerBadge: 'Owner',
    pendingBadge: 'Pending',
    addPlaceholder: 'teammate@example.com',
    addBtn: 'Add',
    addingBtn: 'Adding…',
    helpText:
      "Members see this project, create tickets, and trigger the agent. If the user hasn't signed in yet, the invite stays pending and resolves on their first login.",
    addedToast: 'Added {email}',
    invitedToast: "Invited {email} — they'll join when they sign in",
    addFailedToast: 'Failed to add member',
    confirmRemove: 'Remove {label} from this project?',
    removedToast: 'Member removed',
    removeFailedToast: 'Failed to remove member',
    removeTitle: 'Remove member',
    confirmCancel: 'Cancel invite for {email}?',
    cancelledToast: 'Invite cancelled',
    cancelFailedToast: 'Failed to cancel invite',
    cancelTitle: 'Cancel invite',
    loadFailedToast: 'Failed to load members',
  },
  projectBoard: {
    projectLabel: 'Project',
    noReposTitle: 'No repositories connected',
    noReposBody:
      'Open Settings → Repositories to add a GitLab URL + Personal Access Token. Until then, you cannot create dev branches from a ticket and the agent cannot run.',
    cloningTitle: 'Cloning repositories',
    cloningBodyMulti: 'The agent is fetching: {names}. Progress is streamed into the ticket\'s agent log.',
    cloneFailedTitle: 'Clone failed',
    cloneFailedBody:
      '. Check the URL, token scopes ({api}, {writeRepo}), and branch name, then use ',
    cloneFailedAction: 'Settings → Re-clone',
    loadFailedToast: 'Failed to load project',
    moveFailedToast: 'Failed to move ticket',
    ticketCreatedToast: 'Ticket created',
    ticketCreateFailedToast: 'Failed to create ticket',
    ticketDeletedToast: 'Ticket deleted',
    ticketDeleteFailedToast: 'Failed to delete ticket',
    assigneeUpdateFailedToast: 'Failed to update assignee',
    searchPlaceholder: 'Search tickets…',
    filterByAssignee: 'Filter by assignee',
    filterByCreator: 'Filter by creator',
    filterModeAssignee: 'Assigned',
    filterModeCreator: 'Created by',
    allAssignees: 'All assignees',
    allCreators: 'All creators',
    userFilterSearchPlaceholder: 'Search users…',
    noUsersMatch: 'No users found',
    unassigned: 'Unassigned',
    clearFilters: 'Clear filters',
    moreLabels: 'More labels',
    deleteLabelTitle: 'Delete label',
    deleteLabelConfirmTitle: 'Delete label "{name}"?',
    deleteLabelConfirmDescription: 'The label is removed from every ticket in the project, trash included. Tickets on the board with this label: {count}.',
    deleteLabelConfirmBtn: 'Delete',
    labelDeletedToast: 'Label "{name}" deleted',
    labelDeleteFailedToast: 'Could not delete label',
    noTagsYet: 'No tags yet',
    trashBtn: 'Trash',
    trashTitle: 'Trash',
    trashSubtitle: 'Deleted tickets are kept here. Restore them to put them back on the board, or empty the trash to remove them permanently.',
    trashEmpty: 'Trash is empty.',
    trashLoadFailed: 'Failed to load trash',
    trashRestoreBtn: 'Restore',
    trashPurgeBtn: 'Delete forever',
    trashEmptyBtn: 'Empty trash',
    trashRestoredToast: 'Ticket restored',
    trashRestoreFailedToast: 'Failed to restore ticket',
    trashPurgedToast: 'Ticket permanently deleted',
    trashPurgeFailedToast: 'Failed to permanently delete ticket',
    trashPurgeConfirmTitle: 'Permanently delete ticket?',
    trashPurgeConfirmDescription:
      'Remove ticket T#{number} from this project for good. Comments and agent logs will be removed. This cannot be undone.',
    trashEmptyConfirmTitle: 'Empty trash?',
    trashEmptyConfirmDescription:
      'Every ticket in the trash will be permanently deleted along with its comments and agent logs. This cannot be undone.',
    trashDeletedAt: 'Deleted {date}',
  },
  chat: {
    btn: 'Chat',
    title: 'Project chat',
    emptyTitle: 'No messages yet',
    emptyHint: 'Ask PM to create or update tickets, or about anything on the board.',
    placeholder: 'Message PM…',
    send: 'Send',
    typing: 'PM is responding…',
    noAi: 'You don\'t have AI access in this project — PM won\'t reply to your messages. Ask the owner.',
    placeholderNoAi: 'Message the team…',
  },
  kanban: {
    backlog: 'Backlog',
    inProgress: 'In progress',
    agent: 'Agent',
    review: 'Review',
    blocked: 'Blocked',
    testing: 'Testing',
    done: 'Done',
    agentHint: 'Drop a ticket here to send it to the AI agent — it will create an MR.',
    empty: '— empty —',
  },
  newTicket: {
    title: 'New Ticket',
    description:
      'Describe what needs to be done. Clear, focused descriptions produce better AI fixes.',
    titleLabel: 'Title',
    descriptionLabel: 'Description',
    descriptionPlaceholder: 'Type @ to insert a repo file path.',
    priorityLabel: 'Priority',
    assigneeLabel: 'Assignee',
    unassigned: 'Unassigned',
    labelsLabel: 'Labels (comma-separated)',
    labelsPlaceholder: 'bug, frontend, urgent',
    targetReposLabel: 'Target repos',
    targetReposHint:
      'Highlighted repos will be passed to the agent. If none are selected, all are used.',
    submit: 'Create Ticket',
    submitting: 'Creating...',
    attachBtn: 'Attach file',
    attachAria: 'Attach a file to the description',
    attachUnsupported: 'Unsupported file type',
    attachTooLarge: 'File is too large',
    attachRemoveAria: 'Remove attachment',
    attachmentsLabel: 'Attachments',
  },
  priority: {
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    urgent: 'Urgent',
  },
  ticketCard: {
    ticketNumberTitle: 'Ticket #{number}',
    deleteTitle: 'Delete ticket',
    deleteConfirmTitle: 'Delete ticket?',
    deleteConfirmDescription:
      'Remove "{title}" from this board. This cannot be undone.',
    deleteConfirmBtn: 'Delete ticket',
    agentWorking: 'Agent working',
    mrOpened: 'MR opened',
    agentFailed: 'Agent failed',
    agentPartial: 'Partial',
    queued: 'queued for agent',
    idle: 'idle',
    assignTo: 'Assign to',
    assignedTo: 'Assigned to {name}',
    assignSomeone: 'Assign someone',
    agentStatusTitle: {
      idle: 'Agent idle',
      planning: 'Agent planning',
      awaiting_approval: 'Awaiting plan approval',
      running: 'Agent working',
      success: 'MR opened',
      partial: 'Partial result',
      failed: 'Agent failed',
    },
  },
  ticketDetail: {
    label: 'Ticket',
    closeAria: 'Close panel',
    deleteAria: 'Delete ticket',
    deleteConfirmTitle: 'Delete this ticket?',
    deleteConfirmDescription:
      'Remove ticket T#{number} from this project. Comments and agent logs will be removed. This cannot be undone.',
    deleteConfirmBtn: 'Delete ticket',
    statusLabel: 'Status',
    priorityLabel: 'Priority',
    assigneeLabel: 'Assignee',
    statusBacklog: 'Backlog',
    statusInProgress: 'In progress',
    statusAgent: 'Agent (auto-run)',
    statusBlocked: 'Blocked (waiting for answer)',
    statusTesting: 'Testing',
    statusReview: 'Review',
    statusDone: 'Done',
    descriptionLabel: 'Description',
    descriptionPlaceholder: 'What needs to be done? Type @ to insert a repo file path. Paste images from clipboard.',
    descriptionEditHint: 'Click to edit',
    labelsLabel: 'Tags',
    addLabelPlaceholder: 'Add tag…',
    addLabelPlaceholderEmpty: 'Add tags (press Enter or comma to add)',
    removeLabelAria: 'Remove tag {label}',
    uploadFailedToast: 'Failed to upload image',
    aiTitle: 'AI Agent',
    aiSubtitle: 'Claude Sonnet 4.5',
    viewMr: 'View MR',
    branchLabel: 'Branch',
    copyCheckout: 'Copy git checkout',
    inProgressBranchesTitle: 'Dev branches',
    checkoutCopied: 'Copied: {cmd}',
    copyFailed: 'Copy failed',
    copyContent: 'Copy',
    copyContentHint: 'Copy the whole ticket as text — screenshots come along as download links',
    contentCopied: 'Ticket copied',
    contentCopiedWithImages: 'Ticket copied — {count} screenshot(s) included as download links',
    createBranch: 'Create branch',
    branchCreating: 'Creating…',
    noBranchYet: 'No branch yet',
    branchCreatedToast: 'Branch created',
    branchCreateFailed: 'Could not create branch',
    reviewTitle: 'AI review',
    reviewRun: 'Review changes',
    reviewRerun: 'Re-run review',
    reviewRunning: 'Reviewing…',
    reviewRunningBody: 'Reviewing the branch diff… this can take a moment.',
    reviewHint: 'Run an AI review of this ticket’s dev branch — what changed and potential risks.',
    reviewNoBranch: 'Create a dev branch and push your work first, then run the review.',
    reviewFailedToast: 'Review failed',
    reviewWhatChanged: 'What changed',
    reviewRisks: 'Potential risks',
    reviewPerRepo: 'Per-repo reviews',
    reviewGeneratedAt: 'Reviewed {date}',
    statusPrefix: 'Status: ',
    idle: 'Idle',
    working: 'Working...',
    success: 'All MRs opened',
    partial: 'Partial',
    failed: 'Failed',
    planning: 'Planning…',
    awaitingApproval: 'Awaiting approval',
    runBtn: 'Run Agent',
    retryBtn: 'Retry Agent',
    runningBtn: 'Running...',
    retryRepoBtn: 'Retry',
    runStartedToast: 'Agent started — it will produce a plan first for you to review.',
    runFailedToast: 'Failed to start agent',
    stopBtn: 'Stop',
    stopRequestedToast: 'Stopping agent…',
    stopFailedToast: 'Failed to stop agent',
    approvePlanBtn: 'Approve & code',
    rejectPlanBtn: 'Reject',
    planApprovedToast: 'Plan approved — coding started.',
    planApproveFailedToast: 'Failed to approve plan',
    planRejectedToast: 'Plan rejected.',
    planRejectFailedToast: 'Failed to reject plan',
    planReviewTitle: 'Review plan before coding',
    planReviewBody:
      'The agent paused after planning. Review the plan for each repo and approve to start the coding phase, or reject to cancel and edit the ticket.',
    planFilesLabel: 'Files the planner expects to touch',
    planApproachLabel: 'Approach',
    planRisksLabel: 'Risks / open questions',
    logEmpty: '[ no agent activity yet — click Run Agent ]',
    repoStatus: {
      idle: 'Idle',
      running: 'Running…',
      success: 'MR opened',
      failed: 'Failed',
    },
    commentsTitle: 'Comments ({count})',
    commentPlaceholder: 'Write a comment...',
    commentAttachImage: 'Attach a photo (or paste one)',
    commentSubmit: 'Post',
    commentEditAria: 'Edit comment',
    commentDeleteAria: 'Delete comment',
    commentEditedTag: 'edited',
    commentDeleteConfirmTitle: 'Delete comment?',
    commentDeleteConfirmDescription: 'This comment will be permanently deleted.',
    commentDeleteConfirmBtn: 'Delete',
    commentDeleteFailed: 'Could not delete the comment',
    unknownAuthor: 'Unknown',
    viaSource: 'via {source}',
    createdBy: 'Created by {name}',
  },
  agent: {
    start: 'AI',
    startHint: 'Hand this ticket to the Claude agent',
    resume: 'Resume',
    stop: 'Stop',
    preview: 'Preview',
    openPreview: 'Open preview',
    previewStarting: 'Preview is starting…',
    previewSleeping: 'Preview is asleep — opens in ~1 min',
    previewFailed: 'Preview failed to start',
    title: 'Agent',
    disabledHint: 'The agent is off for this project — enable it in Settings → Agent.',
    noAccess: 'No AI access — ask the project owner',
    offline: 'no signal',
    log: 'Progress',
    mr: 'MR',
    lastError: 'Error',
    startedToast: 'Agent queued',
    stoppedToast: 'Agent stopped',
    previewToast: 'Preview is starting',
    actionFailed: 'Action failed',
    author: 'Claude',
    state: {
      queued: 'Queued',
      understanding: 'Reading',
      waiting_answer: 'Waiting for answer',
      waiting_go: 'Waiting for go-ahead',
      coding: 'Coding',
      testing: 'Testing',
      in_review: 'In review',
      stopped: 'Stopped',
      failed: 'Failed',
      done: 'Done',
    },
    settings: {
      title: 'Agent',
      hint: 'Claude Code works tickets when someone presses «AI». Only the owner sees these settings; secrets are stored encrypted and never shown.',
      enabled: 'Agent enabled',
      model: 'Model',
      modelPlaceholder: 'default (e.g. opus)',
      instructions: 'Agent instructions',
      instructionsPlaceholder: 'What the agent should know about this project in every session',
      limits: 'Limits',
      maxTurns: 'Max turns per run',
      timeout: 'Run timeout, min',
      testLoops: 'Test attempts',
      stack: 'Test stack & preview',
      stackHint: 'Shell scripts, run in the ticket workspace. $CMR_STACK_NAME, $CMR_WORKSPACE, $CMR_PORT_WEB, $CMR_PORT_API, $CMR_PORT_DB are set. The frontend must listen on $CMR_PORT_WEB.',
      stackUp: 'Start (stack_up)',
      stackDown: 'Stop (stack_down)',
      login: 'Test login (Keycloak)',
      keycloakUrl: 'Keycloak URL',
      realm: 'Realm',
      clientId: 'Client ID',
      username: 'Username',
      password: 'Password',
      prod: 'Prod access',
      prodDsn: 'Prod DB DSN',
      kubeContext: 'kube context',
      loki: 'Loki URL',
      git: 'Git (bot)',
      gitUsername: 'Username',
      gitToken: 'Token (push + MR)',
      gitTokenHint: 'Empty — the repo token is used.',
      aiUsers: 'Who can use AI',
      aiUsersHint: 'Run the agent, wake it with comments, and talk to the PM in chat. You (the owner) always can.',
      aiUsersEmpty: 'No other members in this project yet.',
      extraEnv: 'Extra env vars (KEY=VALUE per line)',
      inbox: 'Requests from other apps',
      autoStart: 'Start the agent at once on tickets created via the API (e.g. reports from DM)',
      webhookUrl: 'Webhook URL',
      webhookUrlHint: 'Changes of tickets with an external_id are POSTed here (JSON)',
      webhookSecret: 'Webhook secret',
      webhookSecretHint: 'Signature: X-CMR-Signature = sha256 HMAC of the body',
      secretSet: 'set — type a new value to replace',
      secretEmpty: 'not set',
      clear: 'Clear',
      save: 'Save agent settings',
      saved: 'Agent settings saved',
      loadFailed: 'Could not load agent settings',
    },
  },
  repoMentions: {
    listAria: 'Repository files',
    cloneFirst: 'Repository not cloned yet — file browse will work after the agent\'s first run on this repo.',
    loading: 'Loading files…',
    notReady: 'Repository not ready.',
    loadError: 'Could not load files.',
    noMatch: 'No matching files.',
    pickRepo: 'Pick a repo',
  },
};

export default en;
